import React from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Components
import LoadingArea from '../LoadingArea';
import { BodyMedium, DivWithShadow, DividerLightGreyStyled, LinkSmall, LinkRouter, H4 } from '@styled';

// TODO: shared items should not reference a specific module
import EditProfilePictureLink from '../../../../icsc-app/js/components/_molecules/EditProfilePictureLink';
import ErrorHandler from '../../../../icsc-app/js/components/_molecules/ErrorHandler';
import ProfileStrengthStatus from '../../../../icsc-app/js/components/_molecules/ProfileStrengthStatus';
import ProfileStrengtheningCarousel from '../../../../icsc-app/js/components/_molecules/ProfileStrengtheningCarousel';

// Helpers
import { getMemberMainAddress } from '@general/helpers/member.helpers';
import useMemberTenure from '@hooks/useMemberTenure';
import { useScreenSmDown } from '@hooks/useResponsive';
import useViewer from '@hooks/useViewer';
import styled from 'styled-components';

const StyledImage = styled.img`
    align-self: center;
    margin-left: ${props => (props.isMobile ? '5px' : '0')};
    margin-top: ${props => (props.isMobile ? '0' : '1rem')};
    min-width: 60px;
    max-width: 60px;
`;

const MemberTenureRecognition = ({ isMobile }) => {
    return (
        <StyledImage
            alt="25 Year Member"
            isMobile={isMobile}
            src="/assets/images/member-tenure-recognition-25-profile.svg"
        />
    );
};

const MemberProfileStatusCard = () => {
    const history = useHistory();
    const { loading, error, viewer } = useViewer();
    const tenure = useMemberTenure(viewer?.recordId);
    const isScreenSmDown = useScreenSmDown();
    const displayMemberTenureBadge = tenure !== undefined && tenure !== null && tenure >= 25;
    const profileClasses = classNames('d-flex', {
        'justify-content-between': isScreenSmDown,
    });

    useErrorHandler(error);

    if (loading) {
        return (
            <DivWithShadow>
                <LoadingArea minHeight={470} />
            </DivWithShadow>
        );
    }

    if (!viewer) {
        return null;
    }

    const { id, fullName, title, company, addresses, recordId, status } = viewer;
    const mainAddress = getMemberMainAddress(addresses);

    return (
        <ErrorHandler>
            <DivWithShadow className="px-4 py-4 fade-in-fast" style={{ flexGrow: 1 }}>
                <div className={profileClasses}>
                    <div className="d-flex flex-column align-items-center">
                        <EditProfilePictureLink />
                        {!isScreenSmDown && displayMemberTenureBadge && <MemberTenureRecognition />}
                    </div>
                    <div className="ml-3 ml-md-5">
                        <H4 className="mb-3">
                            <strong>{fullName}</strong>
                        </H4>

                        {title && <BodyMedium className="mb-1 text-background">{title}</BodyMedium>}
                        {mainAddress && (mainAddress.city || mainAddress.stateProvince || mainAddress.country) && (
                            <BodyMedium className="mb-1 text-background">
                                {mainAddress.city}, {mainAddress.stateProvince} {mainAddress.country}
                            </BodyMedium>
                        )}

                        {company && company.name && (
                            <Link
                                to={{ pathname: `/company/profile/${company.recordId}`, state: { inApp: true } }}
                                className="profile-co-link"
                            >
                                <BodyMedium className="mb-3 text-red">{company.name}</BodyMedium>
                            </Link>
                        )}

                        <div>
                            <LinkSmall
                                href="/my-icsc/account/edit-profile"
                                onClick={e => {
                                    e.preventDefault();
                                    history.push('/my-icsc/account/edit-profile');
                                }}
                            >
                                <span>Edit Profile</span>
                            </LinkSmall>
                        </div>

                        {status === 'Active' && (
                            <div>
                                <LinkRouter
                                    to={{
                                        pathname: `/member/profile/${recordId}`,
                                        state: { graphID: id },
                                    }}
                                >
                                    View My Profile
                                </LinkRouter>
                            </div>
                        )}
                    </div>
                    {isScreenSmDown && <div>{displayMemberTenureBadge && <MemberTenureRecognition isMobile />}</div>}
                </div>

                <DividerLightGreyStyled className="mt-1rem mb-4" />

                <ProfileStrengthStatus />

                <div className="mt-4">
                    <ProfileStrengtheningCarousel />
                </div>
            </DivWithShadow>
        </ErrorHandler>
    );
};

// MemberProfileStatusCard.propTypes = {
// };
MemberTenureRecognition.propTypes = {
    isMobile: PropTypes.bool,
};

export default MemberProfileStatusCard;
